// Dashboard cell — overview of queue depths + today's filings + recent
// invariant violations. Pulls from /admin/officer/{id}/queue (no
// `expand` — only counts needed) so it stays cheap.

import { useOfficerQueue } from '../../lib/officer-queue';
import { Pill } from '@rmint/ui';
import { CellShell, StatTile } from './CellShell';

type Props = { officerId: string };

export function DashboardCell({ officerId }: Props) {
  const { data, loading, error } = useOfficerQueue(officerId);

  return (
    <CellShell
      name="dashboard"
      title="Dashboard"
      purpose="Overview: queue depths, in-flight reviews, today's filings."
      status={loading ? <Pill tone="neutral">…</Pill> : error ? <Pill tone="danger">err</Pill> : <Pill tone="success">live</Pill>}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Pending"
          value={data?.pending.length ?? '—'}
          intent={data && data.pending.length > 0 ? 'warn' : 'neutral'}
        />
        <StatTile label="In progress" value={data?.in_progress.length ?? '—'} />
        <StatTile
          label="Completed today"
          value={data?.completed_today.length ?? '—'}
          intent="success"
        />
        <StatTile
          label="Invariant violations"
          value={data?.invariant_violations.length ?? '—'}
          intent={data && data.invariant_violations.length > 0 ? 'warn' : 'neutral'}
        />
      </div>
      {error ? (
        <div className="text-xs text-danger bg-danger-bg border border-danger-line rounded-md p-3">
          {error}
        </div>
      ) : null}
      {data ? (
        <p className="text-xs text-foreground-mute">
          officer <code>{data.officerId}</code> · today <code>{data.today}</code>
          {data.pagination.hasMore ? ' · pagination: more pages available' : ''}
        </p>
      ) : null}
    </CellShell>
  );
}

